import { $httpFhirApi } from '@/common/api/httpFhir.service';
import { $log } from '@/common/api/log.service';
import { useAuthStore } from '@/stores/auth';
import { computed } from 'vue';

// FHIR search parameter selecting Users whose Practitioner has a PractitionerRole
// at one of the listed locations. The chain forward-hops User.practitioner (typed
// as Practitioner) FIRST, then reverse-chains (_has) to PractitionerRole. The
// forward hop is required because PractitionerRole.practitioner references the
// Practitioner, not the User — a bare `_has:PractitionerRole:practitioner:location`
// on User matches nothing (User.id != Practitioner.id). (#4582)
export const LOCATION_SCOPE_PARAM = 'practitioner:Practitioner._has:PractitionerRole:practitioner:location';

// Sentinel location id used when a restricted user has zero assigned locations.
// Querying it keeps the normal DataProvider lifecycle (loading → loaded with
// zero rows) instead of the error-slot path an empty query would trigger, and
// the backend access policy accepts the request because the _has clause is
// present — it just matches nothing.
export const NO_LOCATIONS_SENTINEL = '__bn-no-shared-locations__';

/**
 * Shared-location user-visibility restriction (#4582 / #4616).
 *
 * When the signed-in user carries `restrictToSharedLocations`, every User query
 * the client issues must carry the LOCATION_SCOPE_PARAM clause scoped to the
 * viewer's locations — both so the picker only shows co-located users AND because
 * the Aidbox access policies (Phase 1.5) reject restricted-user User searches that
 * lack the clause.
 *
 * Use `scopeUserQuery` for object-form DataProvider queries and `scopeUserUrl`
 * for string URLs/query-strings. Both are no-ops for unrestricted users, so
 * they are safe to apply unconditionally at every User-query call site.
 */
export function useSharedLocationRestriction() {
    const authStore = useAuthStore();

    // The real store wraps the User resource as { apiUser, user } (auth.js:73 precedent).
    const isRestricted = computed(() => authStore.user?.user?.restrictToSharedLocations === true);

    // practitionerRoles entries are flat { programId, locationId, ... } (auth.js setUserAgencies).
    const restrictedLocationIds = computed(() => {
        const roles = authStore.practitionerRoles || [];
        return [...new Set(roles.map((role) => role?.locationId).filter(Boolean))];
    });

    const locationScopeValue = computed(() => {
        const ids = restrictedLocationIds.value.length > 0 ? restrictedLocationIds.value : [NO_LOCATIONS_SENTINEL];
        return ids.join(',');
    });

    // Comma-joined viewer location ids for the timeline-data custom query's
    // viewer_location_ids param — the ids-or-sentinel value for restricted
    // viewers, undefined for everyone else so the param is omitted entirely.
    const timelineOwnerLocationValue = computed(() => (isRestricted.value ? locationScopeValue.value : undefined));

    /**
     * Scope an object-form query (the `query` object passed to DataProvider).
     * Returns the input untouched for unrestricted users; returns a copy with
     * the _has clause for restricted users.
     */
    const scopeUserQuery = (queryParams) => {
        if (!isRestricted.value) return queryParams;
        return { ...queryParams, [LOCATION_SCOPE_PARAM]: locationScopeValue.value };
    };

    /**
     * Scope the timeline-data custom query's param object. Returns the
     * input untouched for unrestricted users; returns a copy carrying the
     * viewer_location_ids param for restricted users, which the query uses to
     * hide activity owned by users outside the viewer's locations.
     */
    const scopeTimelineParams = (params) => {
        if (!isRestricted.value) return params;
        return { ...params, viewer_location_ids: timelineOwnerLocationValue.value };
    };

    /**
     * Scope a full URL that includes the resource path (e.g. 'User?practitioner=a,b'
     * or 'User'). Appends with '&' when a query string already exists, '?' otherwise.
     */
    const scopeUserUrl = (url) => {
        if (!isRestricted.value) return url;
        const separator = url.includes('?') ? '&' : '?';
        return url + separator + LOCATION_SCOPE_PARAM + '=' + locationScopeValue.value;
    };

    /**
     * Scope a bare query-string fragment with no resource path or '?' (the string
     * form DataProvider accepts in `{ resourceType, query }`, e.g.
     * 'practitioner:missing=false&_include=practitioner'). Joins with '&' when the
     * fragment is non-empty.
     */
    const scopeUserQueryString = (fragment) => {
        if (!isRestricted.value) return fragment;
        const clause = LOCATION_SCOPE_PARAM + '=' + locationScopeValue.value;
        return fragment ? fragment + '&' + clause : clause;
    };

    /**
     * Filter a list of Practitioner results so a restricted viewer can't see
     * STAFF at locations they aren't assigned to (#4697). The global contact
     * search hits Practitioner/$lookup, a custom op that ignores location
     * scoping, so the results are filtered client-side here.
     *
     * Keeps a practitioner when EITHER it has no PractitionerRole (external /
     * non-staff referral contact) OR it has a role at one of the viewer's
     * locations. Drops practitioners that are staff only at out-of-location
     * sites. No-op for unrestricted viewers (and no API call).
     *
     * @param {Array<{id: string}>} practitioners
     * @returns {Promise<Array>} the visible subset
     */
    const filterVisiblePractitioners = async (practitioners) => {
        if (!isRestricted.value) return practitioners;
        if (!practitioners?.length) return [];

        const ids = practitioners.map((p) => p.id).filter(Boolean);
        if (!ids.length) return practitioners;

        let roles;
        try {
            const response = await $httpFhirApi.get('PractitionerRole?practitioner=' + ids.join(',') + '&_elements=practitioner,location');
            roles = (response.data || []).filter((r) => r.resourceType === 'PractitionerRole');
        } catch (error) {
            // Fail closed: without role data we can't tell in-location staff from
            // out-of-location staff, so for a restricted viewer we drop the entire
            // practitioner set rather than risk leaking out-of-location staff on a
            // transient error. The caller still renders Patients/Organizations.
            $log.error('filterVisiblePractitioners: PractitionerRole fetch failed; hiding practitioner results', error?.message, error?.stack);
            return [];
        }

        const allowedLocations = new Set(restrictedLocationIds.value);

        return practitioners.filter((practitioner) => {
            const practitionerRoles = roles.filter((role) => role.practitioner?.id === practitioner.id);
            // No role at all → external/non-staff contact → keep.
            if (!practitionerRoles.length) return true;
            // Staff → keep only if a role is at one of the viewer's locations.
            return practitionerRoles.some((role) => (role.location || []).some((loc) => allowedLocations.has(loc?.id)));
        });
    };

    return {
        isRestricted,
        restrictedLocationIds,
        locationScopeValue,
        timelineOwnerLocationValue,
        scopeUserQuery,
        scopeTimelineParams,
        scopeUserUrl,
        scopeUserQueryString,
        filterVisiblePractitioners,
    };
}
